import React, { useMemo, useRef, useCallback, useState } from 'react';
import { CardData } from '../types';

interface MiniMapProps {
  cards: CardData[];
  pan: { x: number; y: number };
  setPan: (pan: { x: number; y: number } | ((prev: { x: number; y: number }) => { x: number; y: number })) => void;
  scale: number;
  setScale: (scale: number) => void;
  canvasRef: React.RefObject<HTMLDivElement>;
  visible?: boolean;
  isDark?: boolean;
  width?: number;
  height?: number;
  criticalStageId?: string | null;
  pendingActionCardIds?: string[];
  wasteZoneCardIds?: string[];
}

const DEFAULT_W = 150;
const DEFAULT_H = 150;
const PAD = 0;
const VIEWPORT_PAD = 2;

export const MiniMap: React.FC<MiniMapProps> = ({
  cards,
  pan,
  setPan,
  scale,
  canvasRef,
  visible = true,
  isDark = false,
  width = DEFAULT_W,
  height = DEFAULT_H,
  criticalStageId = null,
  pendingActionCardIds = [],
  wasteZoneCardIds = [],
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const lastPanRef = useRef({ x: 0, y: 0 });
  const targetPanRef = useRef({ x: 0, y: 0 });
  const rafRef = useRef<number | null>(null);

  const bounds = useMemo(() => {
    if (!cards.length) return { minX: 0, maxX: 1000, minY: 0, maxY: 800 };
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const c of cards) {
      const x = c.posicao?.x ?? 0;
      const y = c.posicao?.y ?? 0;
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x + 200);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y + 160);
    }
    return { minX, maxX, minY, maxY };
  }, [cards]);

  const mapScale = useMemo(() => {
    const sw = (width - PAD * 2) / (bounds.maxX - bounds.minX);
    const sh = (height - PAD * 2) / (bounds.maxY - bounds.minY);
    return Math.min(sw, sh, 0.18);
  }, [bounds, width, height]);

  // Centering offsets so content is always centered inside the minimap
  const offsetX = useMemo(() => {
    const contentW = (bounds.maxX - bounds.minX) * mapScale;
    return (width - contentW) / 2;
  }, [bounds, mapScale, width]);

  const offsetY = useMemo(() => {
    const contentH = (bounds.maxY - bounds.minY) * mapScale;
    return (height - contentH) / 2;
  }, [bounds, mapScale, height]);

  const viewport = useMemo(() => {
    if (!canvasRef.current) return { x: PAD + VIEWPORT_PAD, y: PAD + VIEWPORT_PAD, w: 40, h: 25 };
    const vw = canvasRef.current.clientWidth;
    const vh = canvasRef.current.clientHeight;
    const rawW = Math.max(16, (vw / scale) * mapScale);
    const rawH = Math.max(10, (vh / scale) * mapScale);
    const viewportCenterX = (((-pan.x + vw / 2) / scale) - bounds.minX) * mapScale + offsetX;
    const viewportCenterY = (((-pan.y + vh / 2) / scale) - bounds.minY) * mapScale + offsetY;
    const rawX = viewportCenterX - rawW / 2;
    const rawY = viewportCenterY - rawH / 2;
    const maxX = width - PAD - VIEWPORT_PAD - rawW;
    const maxY = height - PAD - VIEWPORT_PAD - rawH;
    return {
      x: Math.max(PAD + VIEWPORT_PAD, Math.min(maxX, rawX)),
      y: Math.max(PAD + VIEWPORT_PAD, Math.min(maxY, rawY)),
      w: rawW,
      h: rawH,
    };
  }, [pan, scale, bounds, mapScale, offsetX, offsetY, canvasRef, width, height]);

  const navigate = useCallback((e: React.MouseEvent | MouseEvent, immediate: boolean) => {
    if (!ref.current || !canvasRef.current) return;
    const rect = ref.current.getBoundingClientRect();
    let mx = Math.max(0, Math.min(width, e.clientX - rect.left));
    let my = Math.max(0, Math.min(height, e.clientY - rect.top));
    const cx = (mx - offsetX) / mapScale + bounds.minX;
    const cy = (my - offsetY) / mapScale + bounds.minY;
    const vw = canvasRef.current.clientWidth;
    const vh = canvasRef.current.clientHeight;
    const nx = Math.round(-(cx * scale) + vw / 2);
    const ny = Math.round(-(cy * scale) + vh / 2);
    targetPanRef.current = { x: nx, y: ny };
    if (immediate) {
      lastPanRef.current = { x: nx, y: ny };
      setPan({ x: nx, y: ny });
    } else if (rafRef.current === null) {
      rafRef.current = requestAnimationFrame(() => {
        const cur = lastPanRef.current;
        const tgt = targetPanRef.current;
        const lx = Math.round(cur.x + (tgt.x - cur.x) * 0.35);
        const ly = Math.round(cur.y + (tgt.y - cur.y) * 0.35);
        lastPanRef.current = { x: lx, y: ly };
        setPan({ x: lx, y: ly });
        rafRef.current = null;
      });
    }
  }, [mapScale, offsetX, offsetY, bounds, scale, setPan, canvasRef, width, height]);

  const onDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
    lastPanRef.current = { x: pan.x, y: pan.y };
    navigate(e, true);
    const onMove = (ev: MouseEvent) => navigate(ev, false);
    const onUp = () => {
      setDragging(false);
      if (rafRef.current !== null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [navigate, pan.x, pan.y]);

  if (!visible) return null;

  const pendingSet = new Set(pendingActionCardIds);
  const wasteSet = new Set(wasteZoneCardIds);
  const viewportMaskFill = isDark ? 'rgba(2, 6, 23, 0.08)' : 'rgba(15, 23, 42, 0.055)';
  const viewportStroke = isDark ? 'rgba(148, 163, 184, 0.5)' : 'rgba(71, 85, 105, 0.34)';
  const viewportInnerStroke = isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(255, 255, 255, 0.18)';

  return (
    <div
      ref={ref}
      className="select-none"
      style={{
        width,
        height,
        background: 'transparent',
        border: 'none',
        borderRadius: 0,
        overflow: 'hidden',
        cursor: dragging ? 'grabbing' : 'grab',
        boxShadow: 'none',
        outline: 'none',
      }}
      onMouseDown={onDown}
    >
      <svg width={width} height={height}>
        <defs>
          <filter id="minimap-viewport-inner-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feOffset dx="0" dy="0" />
            <feGaussianBlur stdDeviation="1.2" result="offset-blur" />
            <feComposite in="SourceGraphic" in2="offset-blur" operator="out" result="inverse" />
            <feFlood floodColor={isDark ? '#ffffff' : '#0f172a'} floodOpacity="0.08" result="color" />
            <feComposite in="color" in2="inverse" operator="in" result="shadow" />
            <feComposite in="shadow" in2="SourceGraphic" operator="over" />
          </filter>
        </defs>
        {cards.map(card => {
          const posX = (card.posicao?.x ?? 0);
          const posY = (card.posicao?.y ?? 0);
          const rx = (posX - bounds.minX) * mapScale + offsetX;
          const ry = (posY - bounds.minY) * mapScale + offsetY;
          const isCritical = criticalStageId === card.id;
          const hasPending = pendingSet.has(card.id);
          const hasWaste = wasteSet.has(card.id);

          // Canvas bounding box per card used by bounds: 200 × 160
          // Each shape is centered within that bbox (offsets in canvas-px × mapScale)
          if (card.tipo === 'decisao') {
            // Diamond 160×160 → center offset: x+20, y+0 within 200×160 bbox
            const dw = Math.max(4, 160 * mapScale);
            const dh = Math.max(4, 160 * mapScale);
            const ox = 20 * mapScale; // (200-160)/2
            const oy = 0;             // (160-160)/2
            const cx = rx + ox + dw / 2;
            const cy = ry + oy + dh / 2;
            const fill = 'transparent';
            return (
              <g key={card.id}>
                <polygon
                  points={`${cx},${ry+oy} ${rx+ox+dw},${cy} ${cx},${ry+oy+dh} ${rx+ox},${cy}`}
                  opacity={0.3} fill={fill} stroke={isCritical ? '#ef4444' : (isDark ? '#e2e8f0' : '#475569')} strokeWidth={0.5}
                />
                {hasPending && <circle cx={rx + ox + dw - 3} cy={ry + oy + 4} r={2.2} fill="#f59e0b" />}
                {hasWaste && <circle cx={rx + ox + 4} cy={ry + oy + dh - 4} r={2.2} fill="#a855f7" />}
              </g>
            );
          } else if (card.tipo === 'card_inicial' || card.tipo === 'card_final') {
            // Circle 140×140 → center offset: x+30, y+10 within 200×160 bbox
            const diam = Math.max(4, 140 * mapScale);
            const ox = 30 * mapScale; // (200-140)/2
            const oy = 10 * mapScale; // (160-140)/2
            const cx = rx + ox + diam / 2;
            const cy = ry + oy + diam / 2;
            const r = diam / 2;
            const fill = 'transparent';
            return (
              <g key={card.id}>
                <circle cx={cx} cy={cy} r={r} opacity={0.3} fill={fill} stroke={isCritical ? '#ef4444' : (isDark ? '#e2e8f0' : '#475569')} strokeWidth={0.5} />
                {hasPending && <circle cx={cx + r - 2} cy={cy - r + 2} r={2.2} fill="#f59e0b" />}
                {hasWaste && <circle cx={cx - r + 2} cy={cy + r - 2} r={2.2} fill="#a855f7" />}
              </g>
            );
          } else {
            // Rounded rect 180×100 → center offset: x+10, y+30 within 200×160 bbox
            const rw = Math.max(4, 180 * mapScale);
            const rh = Math.max(3, 100 * mapScale);
            const ox = 10 * mapScale; // (200-180)/2
            const oy = 30 * mapScale; // (160-100)/2
            const fill = 'transparent';
            return (
              <g key={card.id}>
                <rect
                  x={rx + ox}
                  y={ry + oy}
                  width={rw}
                  height={rh}
                  rx={2}
                  opacity={0.3} fill={fill} stroke={isCritical ? '#ef4444' : (isDark ? '#e2e8f0' : '#475569')} strokeWidth={0.5}
                />
                {isCritical && <rect x={rx + ox - 2} y={ry + oy} width={1.5} height={rh} fill="#ef4444" />}
                {hasPending && <circle cx={rx + ox + rw - 3} cy={ry + oy + 4} r={2.2} fill="#f59e0b" />}
                {hasWaste && <circle cx={rx + ox + 4} cy={ry + oy + rh - 4} r={2.2} fill="#a855f7" />}
              </g>
            );
          }
        })}
        <path
          d={`
            M0 0 H${width} V${height} H0 Z
            M${viewport.x} ${viewport.y}
            H${viewport.x + viewport.w}
            V${viewport.y + viewport.h}
            H${viewport.x}
            Z
          `}
          fill={viewportMaskFill}
          fillRule="evenodd"
          style={{ transition: dragging ? 'none' : 'all 0.14s ease-out' }}
        />
        <rect
          x={viewport.x} y={viewport.y}
          width={viewport.w} height={viewport.h}
          rx={4}
          fill={isDark ? 'rgba(255, 255, 255, 0.01)' : 'rgba(255, 255, 255, 0.015)'}
          stroke={viewportStroke}
          strokeWidth={0.75}
          style={{ transition: dragging ? 'none' : 'all 0.14s ease-out' }}
        />
        <rect
          x={viewport.x + 0.5}
          y={viewport.y + 0.5}
          width={Math.max(0, viewport.w - 1)}
          height={Math.max(0, viewport.h - 1)}
          rx={3.5}
          fill="none"
          stroke={viewportInnerStroke}
          strokeWidth={0.4}
          style={{
            transition: dragging ? 'none' : 'all 0.14s ease-out',
          }}
        />
      </svg>
    </div>
  );
};

export default MiniMap;
